import type { ReactNode } from "react"
import { memo, useEffect, useMemo, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { format } from "date-fns"
import { IconCircleDottedUserFill18 } from "nucleo-ui-fill-18"
import {
	IconCheckOutline18 as Check,
	IconUserXmarkOutline18 as UserX,
} from "nucleo-ui-outline-18"

import type { ThreadsGet } from "@productlane/api"
import type { PopoverProps } from "@productlane/ui"
import type { TeamMember } from "@productlane/zero/queries"
import { cn } from "@productlane/lib/src/browser"
import { isOutOfOffice } from "@productlane/lib/src/out-of-office"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { Avatar, Button, Popover, Shortcut, Tooltip } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import { MemberHoverCard } from "@/components/member-hover-card"
import { PresenceAvatar } from "@/components/presence-avatar"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { useSession } from "@/lib/session"
import { useRole } from "@/lib/useRole"

export interface AssigneeSelectProps {
	assigneeId?: string | null
	onChange: (assigneeId: string | null) => void
	className?: string
	align?: PopoverProps["align"]
	modal?: PopoverProps["modal"]
	thread: Pick<ThreadsGet[number], "id" | "title">
	children?: ReactNode
	enableShortcut?: boolean
	open: boolean
	onOpenChange: (value: boolean) => void
	teamMembers: readonly TeamMember[]
}

export const AssigneeSelect = memo(
	({
		onChange,
		assigneeId,
		className,
		align,
		modal,
		thread,
		children,
		enableShortcut = true,
		open,
		onOpenChange,
		teamMembers,
	}: AssigneeSelectProps) => {
		const { role } = useRole()
		const { session } = useSession()
		const navigate = useNavigate()
		const [renderedTeamMembers, setRenderedTeamMembers] =
			useState<boolean>(false)

		useShortcut(
			"a",
			() => {
				onOpenChange(true)
			},
			{
				preventDefault: true,
				enabled: role !== "VIEWER" && enableShortcut,
			},
		)

		useShortcut(
			"i",
			() => {
				if (session) {
					if (assigneeId === session.userId) {
						onChange(null)

						toast.success({
							title: "Un-assigned to you",
							description: `was un-assigned from you.`,
							descriptionAction: {
								label: `${thread.title}`,
								callback: () =>
									void navigate({
										to: "/threads/$id",
										params: { id: thread.id },
									}),
							},
						})
					} else {
						onChange(session?.userId)

						toast.success({
							title: "Assigned to you",
							description: `was assigned to you.`,
							descriptionAction: {
								label: `${thread.title}`,
								callback: () =>
									void navigate({
										to: "/threads/$id",
										params: { id: thread.id },
									}),
							},
						})
					}
				}
			},
			{ preventDefault: true, enabled: role !== "VIEWER" && enableShortcut },
		)

		useEffect(() => {
			if (open === true) {
				setRenderedTeamMembers(true)
			} else {
				setTimeout(() => {
					setRenderedTeamMembers(false)
				}, 200)
			}
		}, [open])

		const assignee = useMemo(() => {
			return teamMembers.find((m) => m.user?.id === assigneeId)
		}, [teamMembers, assigneeId])

		return (
			<Popover
				open={open}
				onOpenChange={onOpenChange}
				modal={modal}
				className={cn("p-0", { hidden: role === "VIEWER" })}
				align={align}
				content={
					<RoleCheck not={["VIEWER"]}>
						<Command
							filter={(_, search, keywords) =>
								keywords?.find((keyword) =>
									keyword
										.toLocaleLowerCase()
										.includes(search.toLocaleLowerCase()),
								)
									? 1
									: 0
							}
						>
							<CommandInput autoFocus placeholder="Choose assignee..." />
							<CommandList
								onClick={(e) => {
									e.preventDefault()
								}}
							>
								<CommandEmpty>No matching assignees found.</CommandEmpty>
								<CommandGroup>
									{assigneeId && (
										<CommandItem
											onClick={(e) => {
												e.preventDefault()
											}}
											onSelect={() => {
												onChange(null)
												onOpenChange(false)
											}}
										>
											<div className="flex items-center gap-2">
												<UserX className="ml-1 h-4 w-4" />
												<span className="flex-1 truncate">Unassign</span>
											</div>
										</CommandItem>
									)}
									{renderedTeamMembers &&
										teamMembers.map((membership) => {
											return (
												<CommandItem
													onClick={(e) => {
														e.preventDefault()
													}}
													{...(membership.user?.name
														? { keywords: [membership.user?.name] }
														: {})}
													value={membership.id}
													key={membership.id}
													onSelect={() => {
														onChange(membership.userId)
														onOpenChange(false)
													}}
												>
													<div className="flex items-center gap-2">
														<MemberHoverCard
															userId={membership.userId}
															side="left"
														>
															<Avatar
																image={membership.user?.imageUrl}
																name={membership.user?.name ?? ""}
																size="xs"
															/>
														</MemberHoverCard>
														<div className="flex min-w-0 flex-1 flex-col">
															<span className="truncate">
																{membership.user?.name}
															</span>
															{isOutOfOffice(membership.outOfOfficeUntil) && (
																<span className="truncate text-xs text-tertiary">
																	{awayNote(membership, teamMembers)}
																</span>
															)}
														</div>
														{assigneeId === membership.userId && (
															<Check className="h-4 w-4" />
														)}
													</div>
												</CommandItem>
											)
										})}
								</CommandGroup>
							</CommandList>
						</Command>
					</RoleCheck>
				}
			>
				{children ? (
					children
				) : (
					<div
						className={cn({
							"pointer-events-none": role === "VIEWER",
						})}
					>
						<AssigneeTriggerHint assigneeId={assignee?.user?.id}>
							<Button
								variant="ghost"
								className={cn(
									"min-w-[145px] flex-shrink justify-start truncate text-xs",
									assigneeId ? "!text-primary" : "!text-tertiary",
									className,
								)}
								iconClassName="!text-tertiary size-4"
								wrapperClassName="-my-1 py-1"
								{...(assignee ? {} : { Icon: IconCircleDottedUserFill18 })}
							>
								{assignee ? (
									<>
										<PresenceAvatar
											userId={assignee?.user?.id}
											image={assignee?.user?.imageUrl}
											name={assignee?.user?.name ?? ""}
											size="xxs"
											dotSize="xs"
											className="mr-2"
										/>
										<span className="truncate">
											{assignee?.user?.name?.split(" ")[0]}
										</span>
									</>
								) : (
									<span className="truncate">Assign</span>
								)}
							</Button>
						</AssigneeTriggerHint>
					</div>
				)}
			</Popover>
		)
	},
)

/**
 * An unassigned thread needs the shortcut; an assigned one is better served by
 * who the teammate is.
 */
function AssigneeTriggerHint({
	assigneeId,
	children,
}: {
	assigneeId: string | undefined
	children: ReactNode
}) {
	if (assigneeId) {
		return (
			<MemberHoverCard userId={assigneeId} side="left">
				{children}
			</MemberHoverCard>
		)
	}

	return (
		<Tooltip
			label={
				<div className="flex gap-1">
					Assign to
					<Shortcut>{ShortcutSymbols.A}</Shortcut>
				</div>
			}
		>
			{children}
		</Tooltip>
	)
}

/** Picking an away teammate still assigns them; the note says who covers until they're back. */
function awayNote(member: TeamMember, teamMembers: readonly TeamMember[]) {
	const cover = teamMembers.find((m) => m.userId === member.outOfOfficeCoverId)
	const coverName = cover?.user?.name?.split(" ")[0]
	const until = `Away until ${format(member.outOfOfficeUntil!, "d MMM")}`
	return coverName ? `${until}. ${coverName} covers.` : until
}
